import { useCallback, useEffect, useRef } from 'react'

/**
 * 防抖：连续触发只执行最后一次，适合搜索、刷新、非关键快捷按钮。
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delayMs = 300,
) {
  const callbackRef = useRef(callback)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current)
    },
    [],
  )

  const cancel = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const schedule = useCallback(
    (...args: TArgs) => {
      cancel()
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        callbackRef.current(...args)
      }, delayMs)
    },
    [cancel, delayMs],
  )

  return Object.assign(schedule, { cancel }) as typeof schedule & { cancel: () => void }
}

/**
 * 节流：窗口期内只执行一次，适合“加载更多”等可重复触发但不应高频触发的按钮。
 */
export function useThrottledCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  intervalMs = 600,
) {
  const callbackRef = useRef(callback)
  const lastRunRef = useRef(0)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return useCallback(
    (...args: TArgs) => {
      const now = Date.now()
      if (now - lastRunRef.current < intervalMs) return
      lastRunRef.current = now
      callbackRef.current(...args)
    },
    [intervalMs],
  )
}
